import { useState, useEffect } from "react";
import { apiClient, useAuth } from "../App";
import { BACKEND_URL } from "../lib/api";
import { useAdminControls } from "../AdminMode";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import EmailIdentityCard from "../components/EmailIdentityCard";
import EmailAllowlistCard from "../components/EmailAllowlistCard";
import { ImageUploadCard } from "../components/ImageUploadCard";
import { BandLogo } from "../components/MediaImages";
import CollapsibleCard from "../components/CollapsibleCard";
import PaymentsCard from "./settings/PaymentsCard";
// iter-70: MemberManagementCard removed — now lives at /members-roles.
import InvoicingSettingsCard from "./settings/InvoicingSettingsCard";
import UniformOptionsCard from "./settings/UniformOptionsCard";
import { Image as ImageIcon } from "lucide-react";
import { 
  Settings as SettingsIcon, 
  Key, 
  Copy, 
  RefreshCw, 
  Users, 
  Shield,
  UserPlus,
  Crown,
  User,
  Trash2,
  AlertTriangle,
  Mail,
  Globe,
  Code,
} from "lucide-react";

const Settings = () => {
  const { user, logout } = useAuth();
  const [band, setBand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [bandSettings, setBandSettings] = useState({ daily_digest_enabled: true });
  const [updatingSettings, setUpdatingSettings] = useState(false);

  // BYO online payments moved to settings/PaymentsCard.jsx (iter-70).
  // Member roles + committee + treasurer + cleanup orphans moved to settings/MemberManagementCard.jsx (iter-66d).
  // Invoice prefix + bank + cheque details moved to settings/InvoicingSettingsCard.jsx (iter-66d).
  // Uniform options moved to settings/UniformOptionsCard.jsx (iter-66d).
  const [embedData, setEmbedData] = useState(null);
  const [loadingEmbed, setLoadingEmbed] = useState(false);
  const [sendingMonthlyDigest, setSendingMonthlyDigest] = useState(false);
  const [sendingDailyDigest, setSendingDailyDigest] = useState(false);
  // Band logo state — bust forces <BandLogo> to refetch after upload/delete; hasLogo gates Remove button
  const [logoBust, setLogoBust] = useState(Date.now());
  const [hasLogo, setHasLogo] = useState(false);

  useEffect(() => {
    if (!user?.band_id) return;
    fetch(`${BACKEND_URL}/api/bands/${user.band_id}/logo`, { method: "HEAD" })
      .then((r) => setHasLogo(r.ok))
      .catch(() => setHasLogo(false));
  }, [user?.band_id, logoBust]);

  const isAdmin = user?.role === "admin";
  const { showAdminControls } = useAdminControls();

  useEffect(() => {
    fetchBandDetails();
    if (isAdmin) {
      fetchBandSettings();
    }
  }, [isAdmin]);

  // BYO payments fetch/save/test extracted to settings/PaymentsCard.jsx (iter-70).
  // toggleCommittee, toggleTreasurer, updateUserRole, handleCleanupOrphans, fetchMembers
  //   → settings/MemberManagementCard.jsx (iter-66d)
  // saveInvoicePrefix, saveBankDetails, saveChequeDetails, formatSortCode
  //   → settings/InvoicingSettingsCard.jsx (iter-66d)
  // addUniformOption, removeUniformOption
  //   → settings/UniformOptionsCard.jsx (iter-66d)

  const fetchBandSettings = async () => {
    try {
      const res = await apiClient.get("/band/settings");
      setBandSettings(res.data);
    } catch (error) {
      console.error("Failed to load band settings");
    }
  };

  const toggleDailyDigest = async () => {
    setUpdatingSettings(true);
    try {
      const newValue = !bandSettings.daily_digest_enabled;
      await apiClient.put("/band/settings", { daily_digest_enabled: newValue });
      setBandSettings({ ...bandSettings, daily_digest_enabled: newValue });
      toast.success(newValue ? "Daily digest emails enabled" : "Daily digest emails disabled");
    } catch (error) {
      toast.error("Failed to update setting");
    } finally {
      setUpdatingSettings(false);
    }
  };

  const toggleMonthlyDigest = async () => {
    setUpdatingSettings(true);
    try {
      const newValue = !bandSettings.monthly_digest_enabled;
      await apiClient.put("/band/settings", { monthly_digest_enabled: newValue });
      setBandSettings({ ...bandSettings, monthly_digest_enabled: newValue });
      toast.success(newValue ? "Monthly member digest enabled" : "Monthly member digest disabled");
    } catch (error) {
      toast.error("Failed to update setting");
    } finally {
      setUpdatingSettings(false);
    }
  };

  const toggleMissingPlayersDigest = async () => {
    setUpdatingSettings(true);
    try {
      const newValue = !bandSettings.missing_players_digest_enabled;
      await apiClient.put("/band/settings", { missing_players_digest_enabled: newValue });
      setBandSettings({ ...bandSettings, missing_players_digest_enabled: newValue });
      toast.success(newValue ? "Weekly Missing Players digest enabled" : "Weekly Missing Players digest disabled");
    } catch (error) {
      toast.error("Failed to update setting");
    } finally {
      setUpdatingSettings(false);
    }
  };

  const [sendingMissingPlayersDigest, setSendingMissingPlayersDigest] = useState(false);
  const sendMissingPlayersDigestNow = async () => {
    if (!window.confirm("Send the Missing Players digest now to all band admins?")) return;
    setSendingMissingPlayersDigest(true);
    try {
      const res = await apiClient.post("/band/send-missing-players-digest");
      toast.success(res.data?.message || "Missing Players digest sent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send digest");
    } finally {
      setSendingMissingPlayersDigest(false);
    }
  };

  // Uniform options helpers moved to settings/UniformOptionsCard.jsx (iter-66d)

  const sendMonthlyDigestNow = async () => {
    if (!window.confirm("Send the monthly digest email now to all band members?")) return;
    setSendingMonthlyDigest(true);
    try {
      const res = await apiClient.post("/band/send-monthly-digest");
      toast.success(res.data?.message || "Monthly digest sent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send monthly digest");
    } finally {
      setSendingMonthlyDigest(false);
    }
  };

  const sendDailyDigestNow = async () => {
    if (!window.confirm("Send the daily admin digest now to all band admins?")) return;
    setSendingDailyDigest(true);
    try {
      const res = await apiClient.post("/band/send-daily-digest");
      const msg = res.data?.message || "Daily digest sent";
      if (res.data?.sent_count > 0) {
        toast.success(msg);
      } else {
        toast.info(msg);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send daily digest");
    } finally {
      setSendingDailyDigest(false);
    }
  };

  const generateEmbedCode = async () => {
    setLoadingEmbed(true);
    try {
      const res = await apiClient.post("/band/embed-token");
      setEmbedData(res.data);
      toast.success("Embed code generated!");
    } catch (error) {
      toast.error("Failed to generate embed code");
    } finally {
      setLoadingEmbed(false);
    }
  };

  const copyEmbedCode = () => {
    if (embedData?.embed_code) {
      navigator.clipboard.writeText(embedData.embed_code);
      toast.success("Embed code copied to clipboard!");
    }
  };

  const revokeEmbedToken = async () => {
    try {
      await apiClient.delete("/band/embed-token");
      setEmbedData(null);
      toast.success("Embed token revoked");
    } catch (error) {
      toast.error("Failed to revoke embed token");
    }
  };

  const fetchBandDetails = async () => {
    try {
      const res = await apiClient.get("/band/my-band");
      setBand(res.data);
    } catch (error) {
      console.error("Failed to load band details");
    } finally {
      setLoading(false);
    }
  };

  // fetchMembers + updateUserRole moved to settings/MemberManagementCard.jsx (iter-66d)

  const copyJoinCode = () => {
    if (band?.join_code) {
      navigator.clipboard.writeText(band.join_code);
      toast.success("Band code copied to clipboard!");
    }
  };

  const regenerateCode = async () => {
    setRegenerating(true);
    try {
      const res = await apiClient.post("/band/regenerate-code");
      setBand({ ...band, join_code: res.data.join_code });
      toast.success("Band code regenerated! Share the new code with your members.");
    } catch (error) {
      toast.error("Failed to regenerate code");
    } finally {
      setRegenerating(false);
    }
  };

  const handleDeleteBand = async () => {
    if (deleteConfirmation !== band?.name) {
      toast.error("Band name doesn't match. Please type it exactly.");
      return;
    }
    
    setDeleting(true);
    try {
      await apiClient.delete("/band/delete", { 
        data: { confirmation: deleteConfirmation } 
      });
      toast.success("Band deleted successfully. Logging you out...");
      setDeleteDialogOpen(false);
      // Wait a moment then logout
      setTimeout(() => {
        logout();
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete band");
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
          <SettingsIcon className="w-8 h-8 text-primary" />
          Band Settings
        </h1>
        <p className="text-muted-foreground mt-2">
          Manage your band&apos;s settings and invite new members
        </p>
      </div>

      {/* Band Info Card */}
      <CollapsibleCard icon={Users} title="Band Information" testId="settings-section-band-info">
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <Label className="text-muted-foreground">Band Name</Label>
              <p className="text-xl font-medium mt-1">{band?.name || "Not set"}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Members</Label>
              <p className="text-xl font-medium mt-1">{band?.member_count || 0}</p>
            </div>
          </div>
      </CollapsibleCard>

      {/* Band Logo Card - Admin Only */}
      {showAdminControls && (
        <CollapsibleCard icon={ImageIcon} title="Band Logo" testId="band-logo-card">
            <ImageUploadCard
              postPath="/band/logo"
              deletePath="/band/logo"
              currentImage={
                <BandLogo
                  bandId={user?.band_id}
                  bust={logoBust}
                  className="w-20 h-20 rounded-xl"
                  fallback={
                    <div className="w-20 h-20 rounded-xl bg-secondary flex items-center justify-center border border-white/5">
                      <ImageIcon className="w-8 h-8 text-muted-foreground" />
                    </div>
                  }
                />
              }
              hasImage={hasLogo}
              onChange={(b) => setLogoBust(b || Date.now())}
              testIdPrefix="band-logo"
              helpText="Shown in the sidebar, dashboard, login splash, PDF exports and at the top of every email. PNG / JPEG / WebP, max 5MB."
            />
        </CollapsibleCard>
      )}

      {/* Join Code Card - Admin Only */}
      {showAdminControls && band?.join_code && (
        <CollapsibleCard icon={Key} title="Band Invite Code" className="border-primary/20" testId="settings-section-invite-code">
            <p className="text-sm text-muted-foreground mb-4">
              Share this code with musicians who want to join your band. They&apos;ll enter it during registration.
            </p>
            
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Input
                  value={band.join_code}
                  readOnly
                  className="text-3xl font-mono tracking-[0.5em] text-center bg-secondary/50 border-primary/30 h-16"
                  data-testid="join-code-display"
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                className="h-16 w-16 border-primary/30"
                onClick={copyJoinCode}
                data-testid="copy-code-btn"
              >
                <Copy className="w-5 h-5" />
              </Button>
            </div>

            <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
              <p className="text-xs text-muted-foreground">
                Regenerate the code if you think it&apos;s been shared with unauthorized people.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={regenerateCode}
                disabled={regenerating}
                className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
                data-testid="regenerate-code-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
                {regenerating ? "Regenerating..." : "Regenerate Code"}
              </Button>
            </div>
        </CollapsibleCard>
      )}

      {/* iter-70: Member Management moved to its own page at /members-roles. */}

      {/* Email Identity Card (slug management) */}
      <EmailIdentityCard isAdmin={showAdminControls} />

      {/* iter-109: Trusted-sender allowlist for inbound aliases */}
      <EmailAllowlistCard isAdmin={showAdminControls} />

      {/* Email Notification Settings Card - Admin Only */}
      {showAdminControls && (
        <CollapsibleCard icon={Mail} iconColor="text-blue-400" title="Email Notifications" className="border-blue-500/20" testId="settings-section-email-notifications">
            <div className="space-y-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Daily Admin Digest</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Daily summary at 4pm covering pending RSVPs for events in the next 2 days and any attendance updates in the last 24 hours. Nothing is sent if there's no activity.
                  </p>
                </div>
                <Switch
                  checked={bandSettings.daily_digest_enabled}
                  onCheckedChange={toggleDailyDigest}
                  disabled={updatingSettings}
                  data-testid="daily-digest-toggle"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={sendDailyDigestNow}
                disabled={sendingDailyDigest}
                className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                data-testid="send-daily-digest-now-btn"
              >
                {sendingDailyDigest ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                ) : (
                  <><Mail className="w-4 h-4 mr-2" /> Send daily digest now</>
                )}
              </Button>
            </div>

            <div className="pt-6 border-t border-white/5 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Monthly Member Digest</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Emails all band members on the 1st of each month. Includes new events since the last send, concerts this month, and anything 2-4 months away. Only shows concerts and cancelled rehearsals.
                  </p>
                </div>
                <Switch
                  checked={bandSettings.monthly_digest_enabled}
                  onCheckedChange={toggleMonthlyDigest}
                  disabled={updatingSettings}
                  data-testid="monthly-digest-toggle"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={sendMonthlyDigestNow}
                disabled={sendingMonthlyDigest}
                className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                data-testid="send-monthly-digest-now-btn"
              >
                {sendingMonthlyDigest ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                ) : (
                  <><Mail className="w-4 h-4 mr-2" /> Send monthly digest now</>
                )}
              </Button>
            </div>

            <div className="pt-6 border-t border-white/5 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Weekly Missing Players Digest</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Monday morning email to all band admins listing upcoming concerts where seats are still uncovered (a member said no / maybe / no response and no dep has accepted). Quietly skipped on weeks with nothing missing.
                  </p>
                </div>
                <Switch
                  checked={!!bandSettings.missing_players_digest_enabled}
                  onCheckedChange={toggleMissingPlayersDigest}
                  disabled={updatingSettings}
                  data-testid="missing-players-digest-toggle"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={sendMissingPlayersDigestNow}
                disabled={sendingMissingPlayersDigest}
                className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                data-testid="send-missing-players-digest-now-btn"
              >
                {sendingMissingPlayersDigest ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                ) : (
                  <><Mail className="w-4 h-4 mr-2" /> Send Missing Players digest now</>
                )}
              </Button>
            </div>
            </div>
        </CollapsibleCard>
      )}

      {/* Invoicing settings - Admin Only — extracted to settings/InvoicingSettingsCard.jsx in iter-66d */}
      {showAdminControls && <InvoicingSettingsCard />}

      {/* Uniform options — extracted to settings/UniformOptionsCard.jsx in iter-66d */}
      {showAdminControls && <UniformOptionsCard />}



      {/* Online Payments — BYO Stripe / SumUp (Admin Only) — extracted to settings/PaymentsCard.jsx in iter-70 */}
      {showAdminControls && <PaymentsCard />}

      {/* Public Concert Widget - Admin Only */}
      {showAdminControls && (
        <CollapsibleCard icon={Globe} iconColor="text-purple-400" title="Public Concert Widget" className="border-purple-500/20" testId="settings-section-concert-widget">
            <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Embed a list of your upcoming concerts on your band&apos;s website. Only confirmed and provisional concerts are shown - cancelled events are hidden.
            </p>
            
            {embedData ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Embed Code (paste into your website HTML)</Label>
                  <div className="flex gap-2">
                    <Textarea
                      value={embedData.embed_code}
                      readOnly
                      className="bg-secondary/50 font-mono text-xs h-20"
                      data-testid="embed-code-textarea"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyEmbedCode}
                      className="border-primary/30"
                      data-testid="copy-embed-code-btn"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Code
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(embedData.embed_url, '_blank')}
                      className="text-purple-400"
                      data-testid="preview-embed-btn"
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={revokeEmbedToken}
                      className="text-red-400 hover:bg-red-500/10"
                      data-testid="revoke-embed-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Regenerate
                    </Button>
                  </div>
                </div>
                
                <div className="text-xs text-muted-foreground bg-secondary/30 p-3 rounded-lg">
                  <Code className="w-4 h-4 inline mr-1" />
                  <strong>JSON API:</strong> {embedData.json_url}
                </div>
              </div>
            ) : (
              <Button
                onClick={generateEmbedCode}
                disabled={loadingEmbed}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="generate-embed-btn"
              >
                {loadingEmbed ? "Generating..." : (
                  <>
                    <Code className="w-4 h-4 mr-2" />
                    Generate Embed Code
                  </>
                )}
              </Button>
            )}
            </div>
        </CollapsibleCard>
      )}

      {/* How to Invite Card */}
      <CollapsibleCard icon={UserPlus} iconColor="text-blue-400" title="How to Invite Members" testId="settings-section-invite-howto">
          <ol className="space-y-4 text-sm">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center font-bold">1</span>
              <span>Share the <strong>band code</strong> above with musicians you want to invite</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center font-bold">2</span>
              <span>They go to the Vivo app and click <strong>&quot;Sign up&quot;</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center font-bold">3</span>
              <span>Select <strong>&quot;Join with Band Code&quot;</strong> and enter the code</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center font-bold">4</span>
              <span>They complete registration and automatically join your band!</span>
            </li>
          </ol>
      </CollapsibleCard>

      {/* Non-admin message */}
      {!showAdminControls && (
        <Card className="glass-card border-yellow-500/20">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Shield className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-medium">Admin Access Required</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Only band admins can view the join code and manage member roles. 
                  Contact your band admin if you need access to these settings.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Danger Zone - Delete Band (Admin Only) */}
      {showAdminControls && (
        <CollapsibleCard icon={AlertTriangle} iconColor="text-red-400" title={<span className="text-red-400">Danger Zone</span>} className="border-red-500/30" testId="settings-section-danger-zone">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-medium text-red-400">Delete Band</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Permanently delete this band and all associated data including all members, 
                  events, players, attendance records, and music library. This action cannot be undone.
                </p>
              </div>
              <Button
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
                className="flex-shrink-0"
                data-testid="delete-band-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Band
              </Button>
            </div>
        </CollapsibleCard>
      )}

      {/* Delete Band Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Delete Band Permanently
            </DialogTitle>
            <DialogDescription className="text-left">
              This will permanently delete:
              <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                <li>The band <strong className="text-foreground">{band?.name}</strong></li>
                <li>All member accounts</li>
                <li>All events and attendance records</li>
                <li>All players and sections</li>
                <li>All music library entries</li>
              </ul>
              <p className="mt-4 text-red-400 font-medium">This action cannot be undone!</p>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="confirm-delete">
                Type <strong className="text-foreground">{band?.name}</strong> to confirm:
              </Label>
              <Input
                id="confirm-delete"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="Enter band name"
                className="bg-secondary/50"
                data-testid="delete-confirmation-input"
              />
            </div>
          </div>
          
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeleteConfirmation("");
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteBand}
              disabled={deleting || deleteConfirmation !== band?.name}
              data-testid="confirm-delete-btn"
            >
              {deleting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Everything
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
